import type { RefObject } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';

import { KioskType, type Place, type SideProps } from '../../../@types';
import { useKioskManager } from '../../../hooks/useKioskManager';
import Icon from '../icon/Icon';
import { IconType } from '../icon/types';
import styles from './pinLocation.module.scss';

type PinLocationProps = SideProps & {
  onClick: (location: Place) => void;
  location: Place;
  active: boolean;
  mapContainerRef: RefObject<HTMLDivElement>;
};

export const PinLocation: React.FC<PinLocationProps> = ({
  onClick,
  location,
  active,
  side = 'right',
  mapContainerRef,
}) => {
  const { kiosk, kioskMode } = useKioskManager();
  const pinRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const popupRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const pinDimension = 30;

  const popUpDimension = useMemo(
    () => ({
      width: kiosk?.model === KioskType.BA_TABLET ? 380 : 330,
      height: kiosk?.model === KioskType.BA_TABLET ? 465 : 500,
    }),
    [kiosk?.model],
  );

  const pinXY = useMemo(() => {
    if (!pinRef.current || !active) return {};
    const pinElement = document.getElementById(`pin-${location.id}`);
    return {
      dstFromTop: parseInt(pinElement!.style.top.replace('px', '')),
      dstFromLeft: parseInt(pinElement!.style.left.replace('px', '')),
    };
  }, [location.id, active]);

  const availableAreaForPopup = useMemo(() => {
    if (
      mapContainerRef.current &&
      pinXY?.dstFromTop &&
      pinXY?.dstFromLeft &&
      active
    ) {
      const mapRect = mapContainerRef.current.getBoundingClientRect();
      return {
        distanceToTop: 0 - pinXY.dstFromTop,
        distanceToLeft: 0 - pinXY.dstFromLeft,
        distanceToBottom: mapRect.height - pinXY.dstFromTop,
        distanceToRight: mapRect.width - pinXY.dstFromLeft,
      };
    }
  }, [mapContainerRef, pinXY, active]);

  useEffect(() => {
    if (popupRef.current && pinRef.current && availableAreaForPopup && pinXY) {
      const popUpCoordinate = {
        vertical: 0,
        horizontal: pinDimension,
      };

      if (availableAreaForPopup.distanceToRight < popUpDimension.width) {
        popUpCoordinate.horizontal = 0 - popUpDimension.width - pinDimension;
      }

      if (availableAreaForPopup.distanceToBottom < popUpDimension.height) {
        popUpCoordinate.vertical =
          availableAreaForPopup.distanceToBottom - popUpDimension.height;
        if (kiosk?.model === KioskType.BA_TABLET) {
          popUpCoordinate.vertical = 0 - popUpDimension.height + pinDimension;
        }
      }

      popupRef.current.style.top = `${popUpCoordinate.vertical}px`;
      popupRef.current.style.left = `${popUpCoordinate.horizontal}px`;
    }
  }, [active, popUpDimension, availableAreaForPopup, pinXY, kiosk?.model]);

  const selectLocation = () => {
    onClick(location);
  };

  return (
    <div
      className={`
        ${styles.pin}
        ${styles[side]} 
        ${kiosk && kioskMode && styles[kiosk.model]}
      `}
      ref={pinRef}
      title={location.name}
    >
      <div className={styles.locationContainer} data-testid="container-pin">
        <div
          className={`${styles.iconPin} ${active && styles.active}`}
          onClick={selectLocation}
        >
          <Icon
            icon={active ? IconType.PIN_ACTIVE : IconType.PIN}
            size="full"
            theme="none"
          />
        </div>
      </div>
      <div className={styles.pinLocationLabel} onClick={selectLocation}>
        {location.short_name || location.name}
      </div>
    </div>
  );
};
